export type VoicePlayerStatus =
  | "idle"
  | "loading"
  | "ready"
  | "playing"
  | "paused"
  | "ended"
  | "error";

export type VoicePlayerSnapshot = {
  status: VoicePlayerStatus;
  currentTime: number;
  duration: number | null;
};

export const INITIAL_VOICE_PLAYER: VoicePlayerSnapshot = {
  status: "idle",
  currentTime: 0,
  duration: null,
};

export function voicePlayerOnLoadStart(current: VoicePlayerSnapshot): VoicePlayerSnapshot {
  if (current.status === "playing" || current.status === "paused") {
    return current;
  }
  return { ...current, status: "loading" };
}

export function voicePlayerOnLoadedMetadata(
  current: VoicePlayerSnapshot,
  duration: number,
): VoicePlayerSnapshot {
  const mediaDuration = Number.isFinite(duration) && duration > 0 ? duration : null;
  const persisted =
    current.duration != null && Number.isFinite(current.duration) && current.duration > 0
      ? current.duration
      : null;
  const safeDuration = mediaDuration ?? persisted;
  const status =
    current.status === "playing" || current.status === "paused" || current.status === "ended"
      ? current.status
      : "ready";
  return { ...current, duration: safeDuration, status };
}

export function voicePlayerFromPersistedDuration(
  durationSeconds: number | null | undefined,
): VoicePlayerSnapshot {
  if (durationSeconds == null || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return INITIAL_VOICE_PLAYER;
  }
  return { status: "ready", currentTime: 0, duration: durationSeconds };
}

export function voicePlayerOnTimeUpdate(
  current: VoicePlayerSnapshot,
  currentTime: number,
): VoicePlayerSnapshot {
  const time = Number.isFinite(currentTime) && currentTime >= 0 ? currentTime : 0;
  return { ...current, currentTime: time };
}

export function voicePlayerTogglePlay(current: VoicePlayerSnapshot): VoicePlayerSnapshot {
  if (current.status === "error") {
    return current;
  }
  if (current.status === "playing") {
    return { ...current, status: "paused" };
  }
  if (current.status === "ended") {
    return { ...current, status: "playing", currentTime: 0 };
  }
  return { ...current, status: "playing" };
}

export function voicePlayerOnEnded(current: VoicePlayerSnapshot): VoicePlayerSnapshot {
  return { ...current, status: "ended", currentTime: 0 };
}

export function voicePlayerOnError(current: VoicePlayerSnapshot): VoicePlayerSnapshot {
  return { ...current, status: "error" };
}

/** AbortError is an interruption, not a hard playback failure. */
export function voicePlayerOnPlayRejected(
  current: VoicePlayerSnapshot,
  errorName: string,
): VoicePlayerSnapshot {
  if (errorName === "AbortError") {
    if (current.status === "playing") {
      return { ...current, status: "paused" };
    }
    return current;
  }
  return { ...current, status: "error" };
}

export function clampSeekTime(time: number, duration: number | null): number {
  if (!Number.isFinite(time) || time < 0) {
    return 0;
  }
  if (duration == null || !Number.isFinite(duration) || duration <= 0) {
    return time;
  }
  return Math.min(duration, time);
}

/** Pointer X → 0..1 along the waveform, honoring RTL. */
export function seekRatioFromPointer(
  clientX: number,
  rect: { left: number; width: number },
  isRtl: boolean,
): number {
  if (!Number.isFinite(rect.width) || rect.width <= 0) {
    return 0;
  }
  const fromLeft = (clientX - rect.left) / rect.width;
  const ratio = isRtl ? 1 - fromLeft : fromLeft;
  return Math.min(1, Math.max(0, ratio));
}
